import { useEffect, useId, useRef, useState, type CSSProperties, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@/hooks/use-media-query';
import { Button } from '@/components/ui/button';
import { SvgMinus, SvgPlus } from '@/components/icons-eataly';

const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

export interface TruncatedBlockContentProps {
    children: ReactNode;
    className?: string;
    linesToRender?: number;
    /** When true, truncation and read more/less apply only below the lg breakpoint. */
    mobileOnly?: boolean;
    'data-testid'?: string;
}

/**
 * Clamps block content to a fixed number of lines with read more / read less controls.
 */
export function TruncatedBlockContent({
    children,
    className,
    linesToRender,
    mobileOnly = false,
    'data-testid': dataTestId,
}: TruncatedBlockContentProps): ReactElement {
    const [isExpanded, setIsExpanded] = useState(false);
    const [canExpand, setCanExpand] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const contentId = useId();
    const { t } = useTranslation('common');
    const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
    const shouldTruncate = linesToRender !== undefined && (!mobileOnly || !isDesktop);
    const isCollapsed = shouldTruncate && !isExpanded;

    useEffect(() => {
        setIsExpanded(false);
    }, [isDesktop, linesToRender, mobileOnly]);

    useEffect(() => {
        const el = contentRef.current;
        if (!el || !shouldTruncate) {
            setCanExpand(false);
            return;
        }

        if (isExpanded) {
            return;
        }

        setCanExpand(el.scrollHeight > el.clientHeight);
    }, [children, isExpanded, linesToRender, shouldTruncate]);

    const clampStyle: CSSProperties | undefined = isCollapsed
        ? {
              display: '-webkit-box',
              WebkitLineClamp: linesToRender,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
          }
        : undefined;

    return (
        <div className={className} data-testid={dataTestId}>
            <div id={contentId} ref={contentRef} style={clampStyle}>
                {children}
            </div>

            {shouldTruncate && canExpand && (
                <Button
                    onClick={() => setIsExpanded((expanded) => !expanded)}
                    hierarchy="tertiary"
                    iconAfter={isExpanded ? <SvgMinus /> : <SvgPlus />}
                    className="body-copy-m-bold mt-6 px-0"
                    size="large"
                    aria-expanded={isExpanded}
                    aria-controls={contentId}>
                    {isExpanded ? t('readLess') : t('readMore')}
                </Button>
            )}
        </div>
    );
}
